/* PROCESS INTELLIGENCE — UI minimale de vérification (pas un clone de Celonis) :
   variantes, goulots, déviations, évolution T0→Tn, drill-down jusqu'aux événements. */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { Activity, GitCompareArrows, Loader2, Send } from "lucide-react";

const days = (s) => (s >= 86400 ? `${Math.round(s / 8640) / 10} j` : `${Math.round(s / 360) / 10} h`);

export default function ProcessIntelligence() {
  const [snaps, setSnaps] = useState([]);
  const [snap, setSnap] = useState(null);
  const [caseView, setCaseView] = useState(null);
  const [compare, setCompare] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadList = () => api.get("/workspace/process/snapshots").then((r) => setSnaps(r.data)).catch(() => {});
  useEffect(() => { loadList(); }, []);

  const discover = async () => {
    setBusy(true);
    try {
      const r = await api.post("/workspace/process/discover", {});
      notify.success(`Snapshot : ${r.data.counts.cases} cas, ${r.data.counts.variants} variantes (${r.data.completeness.completeness})`);
      loadList();
    } catch (e) { notify.error(fmtError(e)); }
    setBusy(false);
  };
  const open = (id) => api.get(`/workspace/process/snapshots/${id}`).then((r) => { setSnap(r.data); setCaseView(null); setCompare(null); }).catch((e) => notify.error(fmtError(e)));
  const drill = (caseId) => api.get(`/workspace/process/snapshots/${snap.id}/case/${caseId}`).then((r) => setCaseView(r.data)).catch((e) => notify.error(fmtError(e)));
  const doCompare = async () => {
    if (snaps.length < 2) return notify.error("Il faut au moins 2 snapshots");
    const r = await api.post("/workspace/process/compare", { a: snaps[1].id, b: snaps[0].id });
    setCompare(r.data);
  };
  const handoff = async () => {
    const r = await api.post("/workspace/process/handoff", { snapshot_id: snap.id });
    notify.success(`${r.data.signals_created} signal(aux) DERIVED transmis à REASON`);
  };

  return (
    <div className="p-6 max-w-4xl space-y-4" data-testid="process-intel-page">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-white flex items-center gap-2">
          <Activity className="w-5 h-5 text-[#378ADD]" /> Process Intelligence
        </h1>
        <div className="flex gap-2">
          <button onClick={discover} disabled={busy} data-testid="pi-discover-btn"
            className="px-3 py-1.5 rounded bg-[#378ADD]/20 border border-[#378ADD]/40 text-[#378ADD] text-xs">
            {busy ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "Découvrir le processus observé"}
          </button>
          <button onClick={doCompare} data-testid="pi-compare-btn"
            className="px-3 py-1.5 rounded border border-white/15 text-zinc-300 text-xs">
            <GitCompareArrows className="w-3 h-3 inline mr-1" />Évolution T0→Tn
          </button>
        </div>
      </div>
      <p className="text-[11px] text-zinc-500">Processus OBSERVÉ reconstruit depuis les événements canoniques — déterministe, 0 LLM, corrélation ≠ causation, drill-down jusqu'à la provenance.</p>

      <div className="flex flex-wrap gap-2" data-testid="pi-snapshots">
        {snaps.map((s) => (
          <button key={s.id} onClick={() => open(s.id)}
            className={`px-2 py-1 rounded text-[11px] border ${snap?.id === s.id ? "border-[#378ADD] text-[#378ADD]" : "border-white/10 text-zinc-400"}`}>
            {new Date(s.computed_at).toLocaleString("fr-CH", { dateStyle: "short", timeStyle: "short" })} · {s.counts.cases} cas · {s.completeness.completeness}
          </button>
        ))}
        {snaps.length === 0 && <span className="text-xs text-zinc-500" data-testid="pi-empty">Aucun snapshot — lancez une découverte.</span>}
      </div>

      {compare && (
        <div className="rounded border border-white/10 bg-white/[0.02] p-3 text-xs space-y-1" data-testid="pi-compare">
          <div className="text-white font-medium">Évolution (2 derniers snapshots)</div>
          <div className="text-zinc-400">Nouvelles activités : <span className="text-[#1D9E75]">{compare.new_activities.join(", ") || "—"}</span> · Disparues : <span className="text-[#E24B4A]">{compare.disappeared_activities.join(", ") || "—"}</span></div>
          {compare.duration_shifts.map((d, i) => (
            <div key={i} className={d.change === "ralentissement" ? "text-[#EF9F27]" : "text-[#1D9E75]"}>
              {d.transition} : {d.change} {d.delta_pct}% ({days(d.median_before_s)} → {days(d.median_after_s)})
            </div>
          ))}
          <div className="text-zinc-500">Rework Δ : {compare.rework_delta} · Goulots avant : {compare.bottlenecks_before.join(", ") || "—"} · après : {compare.bottlenecks_after.join(", ") || "—"}</div>
        </div>
      )}

      {snap && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-xs text-zinc-400">{snap.counts.cases} cas · {snap.counts.variants} variantes · complétude : <b className={snap.completeness.completeness === "COMPLETE" ? "text-[#1D9E75]" : "text-[#EF9F27]"}>{snap.completeness.completeness}</b></div>
            <button onClick={handoff} data-testid="pi-handoff-btn"
              className="px-2 py-1 rounded bg-[#7F77DD]/20 border border-[#7F77DD]/40 text-[#7F77DD] text-[11px]">
              <Send className="w-3 h-3 inline mr-1" />Transmettre à REASON
            </button>
          </div>

          {snap.bottlenecks.length > 0 && (
            <div className="rounded border border-[#EF9F27]/25 bg-[#EF9F27]/5 p-3 text-xs space-y-1" data-testid="pi-bottlenecks">
              <div className="text-[#EF9F27] font-medium">Goulots observés (DERIVED)</div>
              {snap.bottlenecks.map((b, i) => (
                <div key={i} className="text-zinc-300">{b.from} → {b.to} : médiane {days(b.median_s)} ({b.n} cas) — {b.rule}</div>
              ))}
            </div>
          )}

          <div className="space-y-1" data-testid="pi-variants">
            {snap.variants.slice(0, 12).map((v) => (
              <div key={v.signature} className="rounded border border-white/10 bg-white/[0.02] p-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-white font-mono truncate mr-2">{v.signature}</span>
                  <span className="text-zinc-400 shrink-0">{v.n_cases} cas ({Math.round(v.share * 100)}%) · {days(v.median_duration_s)}{v.rework ? ` · rework ${v.rework}` : ""}</span>
                </div>
                {Object.keys(v.outcomes || {}).length > 0 && (
                  <div className="text-[10px] text-zinc-500">outcomes : {JSON.stringify(v.outcomes)} (association observée ≠ causalité)</div>
                )}
                <div className="flex gap-1 mt-1">
                  {v.case_ids_sample.slice(0, 5).map((c) => (
                    <button key={c} onClick={() => drill(c)} data-testid={`pi-case-${c}`}
                      className="text-[10px] px-1.5 py-0.5 rounded border border-white/10 text-zinc-500 hover:text-white">{c.slice(0, 12)}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {snap.deviations.length > 0 && (
            <div className="text-[11px] text-zinc-400" data-testid="pi-deviations">
              Déviations : {snap.deviations.slice(0, 5).map((d, i) => (
                <span key={i} className="text-[#EF9F27]">{d.kind} ({d.signature?.slice(0, 40)}…) · </span>))}
            </div>
          )}

          {caseView && (
            <div className="rounded border border-[#378ADD]/25 bg-[#378ADD]/5 p-3 text-xs space-y-1" data-testid="pi-case-detail">
              <div className="text-[#378ADD] font-medium">Case {caseView.case.case_id} — {days(caseView.case.duration_s)} · {caseView.drilldown}</div>
              {caseView.events.map((e) => (
                <div key={e.event_id} className="text-zinc-300 font-mono">
                  {e.occurred_at?.slice(0, 16)} · {e.event_type} · <span className="text-zinc-500">src={e.source} · prov={JSON.stringify(e.provenance).slice(0, 60)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
